import { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Download,
  Calendar,
  Zap,
  Database,
  RefreshCw,
  Settings,
} from 'lucide-react';

export function CostManagement() {
  const [timeRange, setTimeRange] = useState('7d');
  const [budgetAlert, setBudgetAlert] = useState(true);
  const [expandedComponent, setExpandedComponent] = useState<string | null>(null);

  // Time range multipliers for mock data
  const timeMultipliers: Record<string, number> = {
    '24h': 1 / 7,
    '7d': 1,
    '30d': 30 / 7,
    '90d': 90 / 7,
  };

  const multiplier = timeMultipliers[timeRange] || 1;

  const handleExportCSV = () => {
    const csvContent = [
      ['Component', 'Avg Per Query', 'Total Queries', 'Total Cost', 'Percentage'].join(','),
      ...componentCosts.map(comp =>
        [comp.component, `$${comp.avgPerQuery.toFixed(4)}`, comp.totalQueries, `$${comp.totalCost.toFixed(2)}`, `${comp.percentage}%`].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cost-report-${timeRange}.csv`;
    a.click();
  };

  // Adjust data based on time range
  const baseCost = 234.56;
  const costSummary = {
    totalCost: baseCost * multiplier,
    dailyAverage: 33.51,
    monthlyProjected: 33.51 * 30,
    budget: {
      monthly: 1500,
      used: Math.min(100, (baseCost * multiplier / 1500) * 100 * (30 / (timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90))),
      remaining: 100 - Math.min(100, (baseCost * multiplier / 1500) * 100 * (30 / (timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90))),
    },
    trend: '+12.3%',
  };

  const baseQueries = 12547;
  const adjustedQueries = Math.floor(baseQueries * multiplier);

  const componentCosts = [
    {
      component: 'Query Analyzer (Claude)',
      description: 'Stage 1: Claude Sonnet 4.5 query analysis',
      avgPerQuery: 0.0015,
      totalQueries: adjustedQueries,
      totalCost: 18.82 * multiplier,
      percentage: 8.0,
      model: 'claude-sonnet-4.5',
      avgTokens: 500,
    },
    {
      component: 'Vector Search (Pinecone)',
      description: 'Stage 2: Pinecone vector database queries',
      avgPerQuery: 0.0005,
      totalQueries: adjustedQueries,
      totalCost: 6.27 * multiplier,
      percentage: 2.7,
      model: 'pinecone',
      avgTokens: 0,
    },
    {
      component: 'Reranking (Cohere)',
      description: 'Stage 3: Cohere rerank API',
      avgPerQuery: 0.0020,
      totalQueries: adjustedQueries,
      totalCost: 25.09 * multiplier,
      percentage: 10.7,
      model: 'rerank-multilingual-v3.0',
      avgTokens: 2000,
    },
    {
      component: 'LLM Generation (Claude)',
      description: 'Stage 4: Claude Sonnet 4.5 question generation',
      avgPerQuery: 0.0090,
      totalQueries: adjustedQueries,
      totalCost: 112.92 * multiplier,
      percentage: 48.1,
      model: 'claude-sonnet-4.5',
      avgTokens: 3000,
    },
    {
      component: 'Validation (Claude)',
      description: 'Stage 5: Claude validation checks',
      avgPerQuery: 0.0030,
      totalQueries: adjustedQueries,
      totalCost: 37.64 * multiplier,
      percentage: 16.1,
      model: 'claude-sonnet-4.5',
      avgTokens: 1000,
    },
    {
      component: 'MCP Servers',
      description: 'PDF, OE DB, Regulations, etc.',
      avgPerQuery: 0.0010,
      totalQueries: adjustedQueries,
      totalCost: 12.55 * multiplier,
      percentage: 5.3,
      model: 'various',
      avgTokens: 0,
    },
    {
      component: 'Infrastructure',
      description: 'MongoDB, Redis, hosting',
      avgPerQuery: 0.0016,
      totalQueries: adjustedQueries,
      totalCost: 21.27 * multiplier,
      percentage: 9.1,
      model: 'n/a',
      avgTokens: 0,
    },
  ];

  const dailyCosts = [
    { date: 'Jan 1', queries: 1820, cost: 29.12, avgPerQuery: 0.0160 },
    { date: 'Jan 2', queries: 1654, cost: 26.46, avgPerQuery: 0.0160 },
    { date: 'Jan 3', queries: 1789, cost: 28.62, avgPerQuery: 0.0160 },
    { date: 'Jan 4', queries: 1923, cost: 30.77, avgPerQuery: 0.0160 },
    { date: 'Jan 5', queries: 2105, cost: 33.68, avgPerQuery: 0.0160 },
    { date: 'Jan 6', queries: 1987, cost: 31.79, avgPerQuery: 0.0160 },
    { date: 'Jan 7', queries: 2269, cost: 36.30, avgPerQuery: 0.0160 },
  ];

  const topCostExams = [
    { exam: 'CPA-10', queries: 4832, cost: 77.31, avgPerQuery: 0.0160 },
    { exam: 'CPA-20', queries: 3621, cost: 57.94, avgPerQuery: 0.0160 },
    { exam: 'CEA', queries: 2145, cost: 34.32, avgPerQuery: 0.0160 },
    { exam: 'CGE', queries: 1189, cost: 19.02, avgPerQuery: 0.0160 },
    { exam: 'CGA', queries: 760, cost: 12.16, avgPerQuery: 0.0160 },
  ];

  const optimizationRecommendations = [
    {
      title: 'Switch to Haiku for Validation',
      description: 'Use Claude Haiku instead of Sonnet for simple validation checks',
      potentialSavings: '$15.08/month',
      impact: 'low',
      difficulty: 'easy',
    },
    {
      title: 'Cache Query Analysis Results',
      description: 'Cache query analysis for repeated OE codes',
      potentialSavings: '$7.53/month',
      impact: 'medium',
      difficulty: 'medium',
    },
    {
      title: 'Batch Generation Requests',
      description: 'Generate multiple questions in single LLM call',
      potentialSavings: '$22.61/month',
      impact: 'high',
      difficulty: 'medium',
    },
    {
      title: 'Reduce Reranking Documents',
      description: 'Send top 50 docs to Cohere instead of 100',
      potentialSavings: '$12.55/month',
      impact: 'medium',
      difficulty: 'easy',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cost Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Track and optimize AI model costs across the RAG pipeline
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </button>
          <button className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 flex items-center">
            <Settings className="w-4 h-4 mr-2" />
            Budget Settings
          </button>
        </div>
      </div>

      {/* Budget Alert */}
      {budgetAlert && costSummary.budget.used > 65 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-900">Budget Alert</p>
              <p className="text-sm text-yellow-800 mt-1">
                You've used {costSummary.budget.used}% of your monthly budget ($
                {(costSummary.budget.monthly * costSummary.budget.used / 100).toFixed(2)} / $
                {costSummary.budget.monthly}). Consider implementing cost optimizations.
              </p>
            </div>
          </div>
          <button
            onClick={() => setBudgetAlert(false)}
            className="text-yellow-600 hover:text-yellow-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* Cost Summary */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200 p-6">
          <DollarSign className="w-8 h-8 text-blue-600 mb-2" />
          <p className="text-sm text-gray-600 mb-1">Total Cost ({timeRange === '24h' ? '24 hours' : timeRange === '7d' ? '7 days' : timeRange === '30d' ? '30 days' : '90 days'})</p>
          <p className="text-3xl font-bold text-gray-900">${costSummary.totalCost.toFixed(2)}</p>
          <p className="text-xs text-green-600 mt-1 flex items-center">
            <TrendingDown className="w-3 h-3 mr-1" />
            {costSummary.trend} vs previous period
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <Calendar className="w-8 h-8 text-#ea7500 mb-2" />
          <p className="text-sm text-gray-600 mb-1">Daily Average</p>
          <p className="text-3xl font-bold text-gray-900">${costSummary.dailyAverage.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-1">Per day</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <TrendingUp className="w-8 h-8 text-orange-600 mb-2" />
          <p className="text-sm text-gray-600 mb-1">Monthly Projected</p>
          <p className="text-3xl font-bold text-gray-900">${costSummary.monthlyProjected.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-1">At current rate</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6">
          <AlertCircle className="w-8 h-8 text-green-600 mb-2" />
          <p className="text-sm text-gray-600 mb-1">Budget Remaining</p>
          <p className="text-3xl font-bold text-gray-900">${(costSummary.budget.monthly - (costSummary.budget.monthly * costSummary.budget.used / 100)).toFixed(2)}</p>
          <div className="mt-2">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>{costSummary.budget.used}% used</span>
              <span>${costSummary.budget.monthly} budget</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${
                  costSummary.budget.used > 80
                    ? 'bg-red-500'
                    : costSummary.budget.used > 65
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${costSummary.budget.used}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Component Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Zap className="w-5 h-5 text-primary-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Cost Breakdown by Component</h3>
                <p className="text-xs text-gray-600">{timeRange === '24h' ? 'Last 24 hours' : timeRange === '7d' ? 'Last 7 days' : timeRange === '30d' ? 'Last 30 days' : 'Last 90 days'} • {componentCosts.reduce((sum, c) => sum + c.totalQueries, 0).toLocaleString()} queries</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-7 gap-1 mb-6">
            {componentCosts.map((component, idx) => (
              <div
                key={idx}
                className={`h-32 rounded-lg flex items-end justify-center pb-2 ${
                  idx === 0
                    ? 'bg-blue-500'
                    : idx === 1
                    ? 'bg-#f09628'
                    : idx === 2
                    ? 'bg-green-500'
                    : idx === 3
                    ? 'bg-orange-500'
                    : idx === 4
                    ? 'bg-#f09628'
                    : idx === 5
                    ? 'bg-#0055b2'
                    : 'bg-gray-500'
                }`}
                style={{ height: `${component.percentage * 2}px` }}
                title={`${component.component}: ${component.percentage}%`}
              >
                <span className="text-xs font-bold text-white">{component.percentage}%</span>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            {componentCosts.map((component, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg border border-gray-200 hover:shadow-sm transition-all">
              <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => setExpandedComponent(expandedComponent === component.component ? null : component.component)}
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <div
                      className={`w-3 h-3 rounded ${
                        idx === 0
                          ? 'bg-blue-500'
                          : idx === 1
                          ? 'bg-#f09628'
                          : idx === 2
                          ? 'bg-green-500'
                          : idx === 3
                          ? 'bg-orange-500'
                          : idx === 4
                          ? 'bg-#f09628'
                          : idx === 5
                          ? 'bg-#0055b2'
                          : 'bg-gray-500'
                      }`}
                    />
                    <h4 className="text-sm font-semibold text-gray-900">{component.component}</h4>
                    {component.model !== 'n/a' && component.model !== 'various' && (
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded">
                        {component.model}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 ml-6">{component.description}</p>
                </div>

                <div className="text-right ml-6">
                  <p className="text-lg font-bold text-gray-900">${component.totalCost.toFixed(2)}</p>
                  <p className="text-xs text-gray-600">${component.avgPerQuery.toFixed(4)}/query</p>
                  {component.avgTokens > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">{component.avgTokens} tokens avg</p>
                  )}
                </div>
              </div>
              {expandedComponent === component.component && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-200 bg-white">
                  <p className="text-xs font-medium text-gray-700 mb-2">Breakdown Details</p>
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-gray-500 mb-1">Total Queries</p>
                      <p className="font-semibold text-gray-900">{component.totalQueries.toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-gray-500 mb-1">Cost Per Query</p>
                      <p className="font-semibold text-gray-900">${component.avgPerQuery.toFixed(4)}</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-gray-500 mb-1">Total Cost</p>
                      <p className="font-semibold text-gray-900">${component.totalCost.toFixed(2)}</p>
                    </div>
                  </div>
                  {component.avgTokens > 0 && (
                    <div className="mt-2 p-2 bg-blue-50 rounded">
                      <p className="text-xs text-blue-700">
                        <span className="font-medium">Avg Tokens:</span> {component.avgTokens.toLocaleString()} per query
                      </p>
                    </div>
                  )}
                </div>
              )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Daily Cost Trend */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <TrendingUp className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Daily Cost Trend</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {dailyCosts.map((day) => (
                <div key={day.date} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 w-20">{day.date}</span>
                  <div className="flex-1 mx-4">
                    <div className="w-full h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 flex items-center justify-end pr-2"
                        style={{ width: `${(day.cost / 40) * 100}%` }}
                      >
                        <span className="text-xs font-medium text-white">${day.cost.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 w-32 text-right">
                    {day.queries} queries
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cost by Exam Type */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <Database className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Top Cost by Exam</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {topCostExams.map((exam, idx) => (
                <div
                  key={exam.exam}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg font-bold text-gray-400">#{idx + 1}</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{exam.exam}</p>
                      <p className="text-xs text-gray-600">{exam.queries} queries</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">${exam.cost.toFixed(2)}</p>
                    <p className="text-xs text-gray-600">${exam.avgPerQuery.toFixed(4)}/query</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Optimization Recommendations */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center space-x-3">
            <RefreshCw className="w-5 h-5 text-green-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Cost Optimization Recommendations</h3>
              <p className="text-xs text-gray-600">Potential savings: $57.77/month</p>
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {optimizationRecommendations.map((rec, idx) => (
            <div key={idx} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-sm font-semibold text-gray-900">{rec.title}</h4>
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        rec.impact === 'high'
                          ? 'bg-green-100 text-green-700'
                          : rec.impact === 'medium'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {rec.impact} impact
                    </span>
                    <span className="px-2 py-0.5 bg-fk-orange-vibrant100 text-#d66a00 text-xs rounded">
                      {rec.difficulty}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{rec.description}</p>
                </div>
                <div className="text-right ml-6">
                  <p className="text-lg font-bold text-green-600">{rec.potentialSavings}</p>
                  <button className="mt-2 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700">
                    Implement
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
